import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ShieldCheck, Certificate, UmbrellaSimple, FileText, UsersThree, Handshake, Crosshair, Megaphone, Wallet } from '@phosphor-icons/react';
import { MaskedLines, Reveal, Chapter, EASE } from '../lib/motion';
import { EditorialMarquee } from '../components/site/Marquee';
import { CATEGORIES, COUNTRIES, IMG, HOME_SLUGS, SECTORS } from '../data/site';
import { useSEO } from '../lib/seo';

const trustItems = [
  { icon: ShieldCheck, title: 'Business registry checks', text: "Every provider's registration is verified against their country's public registry — ACRA in Singapore, SSM in Malaysia, and equivalents elsewhere — before they're matched to any RFQ." },
  { icon: Certificate, title: 'Safety credentials review', text: 'Safety credentials are reviewed with each provider during onboarding, so your EHS team has nothing to chase.' },
  { icon: UmbrellaSimple, title: 'Insurance confirmation', text: 'Insurance cover is confirmed with every provider before they quote — not after an incident.' },
];

const buyerSteps = [
  { no: '01', icon: FileText, title: 'Post your RFQ', text: 'Sector, service, scope, drawings, budget band, deadline. Two minutes, one form.' },
  { no: '02', icon: UsersThree, title: 'Get matched quotes', text: 'Founder-reviewed matching to providers in your trade and market — targeting 24–48 hours, subject to provider availability.' },
  { no: '03', icon: Handshake, title: 'Pick and proceed', text: 'Compare like-for-like quotes, award directly. Free for buyers during the pilot.' },
];

const providerSteps = [
  { no: '01', icon: Crosshair, title: 'Join as founding provider', text: 'Tell us your business registration and services. Founder verifies you personally.' },
  { no: '02', icon: Megaphone, title: 'Receive matched RFQs', text: 'Pre-qualified enquiries that fit your capability. No cold outreach, ever.' },
  { no: '03', icon: Wallet, title: 'Lock the rate, pay later', text: 'No payment during the pilot. Founding members are billed SGD 29/mo only when the platform launches — locked for as long as the subscription stays active.' },
];

const StepCard = ({ step, delay }) => (
  <Reveal delay={delay} className="group relative border border-line bg-surface p-8 transition-colors duration-300 hover:border-volt/50">
    <span className="font-mono text-5xl font-semibold text-line transition-colors duration-300 group-hover:text-volt/30">{step.no}</span>
    <step.icon size={26} className="mt-6 text-volt" />
    <h3 className="mt-4 font-display text-lg font-bold text-parchment">{step.title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-steel">{step.text}</p>
  </Reveal>
);

const Home = () => {
  useSEO({
    title: 'Vox Meridian | Global RFQ Marketplace',
    description: 'Post one RFQ and get matched with verified providers across 65 services and 10 markets. Free for buyers during the pilot.',
    path: '/',
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="noise-overlay relative overflow-hidden border-b border-line bg-ink bg-grid">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-20 pt-32 sm:pt-40 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-28">
          <motion.div style={{ opacity: contentOpacity }} className="relative z-10 lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mono-label flex items-center gap-3 text-volt"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-volt" />
              10 markets · Concierge phase live
            </motion.p>
            <h1 className="mt-7 font-display text-[2.6rem] font-black leading-[1.0] tracking-tight text-parchment sm:text-6xl lg:text-7xl">
              <MaskedLines
                lines={[
                  <>The Global RFQ</>,
                  <>Marketplace for</>,
                  <>Industrial &amp;</>,
                  <span key="l4">Business <span className="text-volt">Services.</span></span>,
                ]}
                delay={0.15}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-steel sm:text-lg"
            >
              Industrial services, automation retrofit, data-centre M&amp;E, commercial property
              maintenance, IT support, accounting &amp; payroll — 65 services across 10 markets. Post
              one RFQ and get matched with verified providers, or join as a founding provider and
              receive pre-qualified work.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <Link
                to="/providers"
                data-testid="hero-cta-provider"
                className="group flex items-center justify-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
              >
                Join as Founding Provider
                <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/buyers"
                data-testid="hero-cta-buyer"
                className="group flex items-center justify-center gap-2 border border-line bg-surface/50 px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment transition-colors duration-200 hover:border-volt hover:text-volt"
              >
                Post an RFQ
                <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-[0.15em] text-steel"
              data-testid="hero-stats"
            >
              <span><span className="text-volt">30</span> founding spots</span>
              <span><span className="text-volt">65</span> services</span>
              <span><span className="text-volt">10</span> markets</span>
            </motion.div>
          </motion.div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: EASE }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="duotone clip-frame aspect-[4/5] border border-line" data-testid="hero-image">
                <img src={IMG.machining} alt="Precision machining on a job shop floor" className="h-full w-full object-cover" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.1, ease: EASE }}
                className="absolute -bottom-6 -left-4 z-10 border border-line bg-ink p-5 sm:-left-10"
                data-testid="hero-spec-card"
              >
                <p className="mono-label text-volt">Illustrative example</p>
                <p className="mt-2 font-display text-sm font-bold text-parchment">CNC turning — Inconel 718</p>
                <p className="mt-1 font-mono text-xs text-steel">What a matched RFQ looks like · Tuas, SG</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARKET STRIP */}
      <section className="border-b border-line bg-surface" data-testid="country-strip">
        <div className="mx-auto max-w-7xl px-6 py-7 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
              <span className="mono-label flex items-center gap-2 text-volt">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-volt" />
                Now matching in
              </span>
              {COUNTRIES.map((c) => (
                <span key={c.code} data-testid={`country-chip-${c.code}`} className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-steel">
                  {c.name}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-line bg-surface">
        <div className="mx-auto grid max-w-7xl sm:grid-cols-3">
          {trustItems.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.1} className={`border-line p-8 sm:p-10 ${i > 0 ? 'border-t sm:border-l sm:border-t-0' : ''}`}>
              <t.icon size={28} className="text-volt" />
              <h3 className="mt-4 font-display text-base font-bold text-parchment" data-testid={`trust-${t.title.toLowerCase().replace(/\W+/g, '-')}`}>{t.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{t.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VERIFICATION STANDARD */}
      <section className="border-b border-line bg-ink" data-testid="verification-standard">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="mono-label text-volt">The verification standard</p>
            <h2 className="mt-4 font-display text-4xl font-black tracking-tight text-parchment sm:text-5xl">
              Checked before they ever quote.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-steel">
              Every provider passes the same four checks before being matched to a single RFQ —
              each check recorded with what was verified and when, and re-checked when circumstances
              change.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ShieldCheck, t: 'Registry check', d: 'Business registration verified against the country’s public registry — ACRA in Singapore, SSM in Malaysia, trade licence in the UAE, CRN in the UK, ABN in Australia, and equivalents elsewhere.' },
              { icon: Certificate, t: 'Licence & cover', d: 'Relevant licences, certifications and insurance recorded with scope and expiry dates — before matching, not after an incident.' },
              { icon: FileText, t: 'Capability proof', d: 'Recent job examples in the specific service they are matched for — not a logo wall, evidence of the actual work.' },
              { icon: Handshake, t: 'Founder call', d: 'Every provider is spoken to personally before activation. If anything feels off, they do not join.' },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.07} className="bg-ink p-8" data-testid={`verify-${v.t.toLowerCase().replace(/\W+/g, '-')}`}>
                <v.icon size={26} className="text-volt" />
                <h3 className="mt-4 font-display text-base font-bold text-parchment">{v.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-steel">{v.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-xs leading-relaxed text-steel/60">
              Verification is a genuine reasonable-efforts check, not a guarantee of work, solvency or
              conduct — buyers should apply normal procurement diligence before awarding. Stated plainly
              in our <a href="/terms" className="underline transition-colors duration-200 hover:text-volt">Terms</a>.
            </p>
          </Reveal>
        </div>
      </section>


      {/* HOW IT WORKS */}
      <section className="border-b border-line bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Chapter no="01" label="How it works" title="Two sides. One form each." />
          <Reveal delay={0.1} className="mt-12">
            <div className="mx-auto max-w-3xl">
              <div className="mb-3 flex items-center gap-3">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-volt" />
                <span className="mono-label">Watch — how it works in 15 seconds</span>
              </div>
              <video
                data-testid="intro-video"
                className="w-full border border-line bg-surface"
                controls
                poster="/videos/poster.jpg"
                preload="none"
                playsInline
              >
                <source src="/videos/intro.mp4" type="video/mp4" />
              </video>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-14 lg:grid-cols-2 lg:gap-10">
            <div>
              <Reveal><p className="mono-label mb-6 text-volt">For buyers</p></Reveal>
              <div className="space-y-4">
                {buyerSteps.map((s, i) => <StepCard key={s.no} step={s} delay={i * 0.1} />)}
              </div>
            </div>
            <div>
              <Reveal><p className="mono-label mb-6 text-volt">For providers</p></Reveal>
              <div className="space-y-4">
                {providerSteps.map((s, i) => <StepCard key={s.no} step={s} delay={i * 0.1} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* CATEGORIES */}
      <section className="border-b border-line bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Chapter no="02" label="Service categories" title="Ten core trades. Verified providers." />
          <div className="mt-14 grid gap-4 md:grid-cols-6">
            {CATEGORIES.filter((c) => HOME_SLUGS.includes(c.slug)).map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.07} className={i < 2 ? 'md:col-span-3' : 'md:col-span-2'}>
                <Link
                  to={`/services/${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group block border border-line bg-surface transition-colors duration-300 hover:border-volt/60"
                >
                  <div className="duotone h-44">
                    <img src={c.image} alt={c.name} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-display text-lg font-bold text-parchment transition-colors duration-200 group-hover:text-volt">{c.name}</h3>
                      <ArrowUpRight size={18} className="mt-1 shrink-0 text-steel transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-volt" />
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-steel">{c.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-8">
            <div className="flex flex-wrap items-center justify-center gap-2" data-testid="sector-strip">
              <span className="mono-label mr-2 text-steel">Specialist sectors:</span>
              {SECTORS.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`sector-chip-${s.slug}`}
                  className="border border-line px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-steel transition-colors duration-200 hover:border-volt/60 hover:text-volt"
                >
                  {s.name}
                  {(s.slug === 'automation-retrofit' || s.slug === 'data-centre-mep') && (
                    <span className="ml-2 text-volt">New</span>
                  )}
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal className="mt-10 text-center">
            <Link
              to="/services"
              data-testid="view-all-services-link"
              className="group inline-flex items-center gap-2 border border-line px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment transition-colors duration-200 hover:border-volt hover:text-volt"
            >
              View the full directory — 65 services
              <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FOUNDING OFFER */}
      <section className="border-b border-line bg-volt text-ink">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="mono-label text-ink/70">/03 · Founding provider offer</p>
                <h2 className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tight sm:text-7xl">
                  SGD 29/mo.<br />Locked for life.
                </h2>
                <p className="mt-3 max-w-md font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-ink/60" data-testid="founding-rate-condition">
                  For as long as your subscription stays active · No obligation to subscribe later
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-8 max-w-xl text-base font-medium leading-relaxed text-ink/80 sm:text-lg">
                  The first 30 providers lock the founding rate — while the standard Pro plan
                  launches at SGD 59/mo. Pre-qualified RFQs, founder-verified buyers, no lead fees,
                  no commission. Nothing is billed during the concierge phase — billing starts only when
                  the full platform launches, and founding members keep SGD 29/mo for as long as their
                  subscription stays active. When the 30 spots are gone, they are gone.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-4">
              <div className="border-2 border-ink p-8">
                <p className="font-mono text-xs font-bold uppercase tracking-[0.2em]">Founding allocation</p>
                <p className="mt-3 font-display text-6xl font-black" data-testid="founding-spots-counter">30<span className="text-2xl"> spots</span></p>
                <p className="mt-2 text-sm font-medium text-ink/70">First come, verified in order of application.</p>
                <Link
                  to="/providers"
                  data-testid="founding-cta"
                  className="group mt-8 flex items-center justify-center gap-2 bg-ink px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-volt transition-transform duration-200 hover:-translate-y-0.5"
                >
                  Claim your spot
                  <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BUYER PITCH */}
      <section className="bg-ink">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-32">
          <Reveal className="order-2 lg:order-1">
            <div className="duotone clip-frame-tr aspect-[4/3] border border-line" data-testid="buyer-pitch-image">
              <img src={IMG.automation} alt="Automation integrator at work on a factory floor" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Chapter no="04" label="For buyers" title="Post once. Stop chasing quotes." />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-steel">
                Procurement chase ends here. Post your RFQ with drawings and specs attached — the
                founder reviews and matches it with verified providers in your market, targeting
                24–48 hours, subject to provider availability. Free for buyers during the pilot phase.
              </p>
              <Link
                to="/buyers"
                data-testid="buyer-pitch-cta"
                className="group mt-10 inline-flex items-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
              >
                Post an RFQ — it&rsquo;s free
                <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
